import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box, Button, Stack, TextField, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, AttachFile as AttachFileIcon } from '@mui/icons-material';

import { Container, LoadingState } from '@/components/common';
import { useCreateLibraryPost } from '@/hooks/boards.hook';
import { successToast, errorToast } from '@/common/toast';

const ALLOWED_FILE_TYPES = ['.pdf', '.ppt', '.pptx', '.doc', '.docx'];
const DEFAULT_CATEGORIES = [
  { id: 1, name: '일반-일반'},
  { id: 2, name: '일반-기타'},
  { id: 3, name: '학습실-소프트웨어 개발보안 교육'},
  { id: 4, name: '학습실-정보보안 교육'},
  { id: 5, name: '학습실-기타 관련 교육'},
];

/**
 * 자료실 등록 페이지
 */
const LibraryAddPage = () => {
  const navigate = useNavigate();
  const createLibraryMutation = useCreateLibraryPost();

  const categories = DEFAULT_CATEGORIES;
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      categoryId: 1,
      title: '',
      content: '',
      file: null,
    },
  });

  const validateFile = useCallback((files) => {
    if (!files?.length) return '첨부파일은 필수입니다.';
    const file = files[0];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(ext)) {
      return 'pdf, ppt, pptx, doc, docx 파일만 업로드 가능합니다.';
    }
    return true;
  }, []);

  const onSubmit = useCallback(
    async (data) => {
      try {
        await createLibraryMutation.mutateAsync({
          categoryId: Number(data.categoryId),
          title: data.title,
          content: data.content,
          file: data.file?.[0] ?? null,
        });
        successToast('자료가 등록되었습니다.');
        navigate('/community/library');
      } catch (err) {
        errorToast(`자료 등록에 실패했습니다. - ${err.response?.data?.msg}`);
      }
    },
    [createLibraryMutation, navigate]
  );

  return (
    <Container title="자료 등록">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/community/library')}
        >
          목록으로
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || createLibraryMutation.isPending}
        >
          등록
        </Button>
      </Stack>

      <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Controller
          control={control}
          name="categoryId"
          rules={{ required: '카테고리를 선택해주세요.' }}
          render={({ field }) => (
            <FormControl fullWidth required error={!!errors.categoryId}>
              <InputLabel>카테고리</InputLabel>
              <Select {...field} label="카테고리">
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="title"
          rules={{ required: '제목을 입력해주세요.' }}
          render={({ field }) => (
            <TextField
              {...field}
              label="제목"
              fullWidth
              required
              error={!!errors.title}
              helperText={errors.title?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="content"
          rules={{ required: '내용을 입력해주세요.' }}
          render={({ field }) => (
            <TextField
              {...field}
              label="내용"
              fullWidth
              multiline
              rows={10}
              required
              error={!!errors.content}
              helperText={errors.content?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="file"
          rules={{ validate: validateFile }}
          render={({ field: { onChange, value, ref, ...rest } }) => (
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
                sx={{ mb: 1 }}
              >
                첨부파일 선택 (필수)
                <input
                  type="file"
                  hidden
                  ref={ref}
                  accept=".pdf,.ppt,.pptx,.doc,.docx"
                  onChange={(e) => onChange(e.target.files)}
                  {...rest}
                />
              </Button>
              {value?.[0] && (
                <Box component="span" sx={{ ml: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
                  {value[0].name}
                </Box>
              )}
              {errors.file && (
                <Box component="span" sx={{ display: 'block', mt: 1, fontSize: '0.75rem', color: 'error.main' }}>
                  {errors.file.message}
                </Box>
              )}
            </Box>
          )}
        />
      </Box>
    </Container>
  );
};

export default LibraryAddPage;
